import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import UserForm from '../../components/UserForm';
import UserTable from '../../components/UserTable';
import { Project } from '../../types/projectTypes';
import { User } from '../../types/userTypes';
import { createUser, getProjectsAllCountries, getUsers, ProjectsToUser, getProjectsUser, updateUser, getRols, deleteUserProjects, deleteUser, updateUserProjects } from '../../api/userApi';
import { Positions } from '../../types/EmployeeTypes';
import { getPositions } from '../../api/parametersApi';
import { rols } from '../../types/rolsTypes';
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { Country } from '../../types/countryTypes';
import { getMyCountries, updateUserCountries, getUserCountries } from '../../api/countryApi';

const Usuarios: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [positions, setPositions] = useState<Positions[]>([]);
  const [roles, setRoles] = useState<rols[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [userRole, setUserRole] = useState<string>("");

  const isAdminSistema = userRole === ADMIN_SISTEMA;
  // Gerente y Admin de Sistema gestionan usuarios (crear/editar/eliminar).
  const isManager = userRole === profiles[2].name || isAdminSistema;

  const fetchUsers = async () => {
    try {
      // 1. Obtener rol actual
      const userStr = localStorage.getItem('user');
      if (userStr) {
        setUserRole(JSON.parse(userStr).rols?.description || "");
      }

      const fetchedUsers = await getUsers();

      // FILTRO DE ACTIVOS: usuarios activos o pendientes de aprobación.
      const activeUsers = fetchedUsers.filter((u: any) =>
        u.active === 1 ||
        u.active === true ||
        (u.identificationNum && u.identificationNum.startsWith('PENDING-'))
      );

      const positionData = await getPositions();
      setPositions(positionData);

      const rolesData = await getRols();
      setRoles(rolesData);

      // Países que el usuario puede administrar: Gerente = los suyos; Admin Sistema = todos.
      const countriesData = await getMyCountries();
      setCountries(countriesData);
      const myCountryIds = new Set(countriesData.map((c) => c.id));

      const userWithProjects = await Promise.all(
        activeUsers.map(async (user) => {
          const projectsResponse = await getProjectsUser(user.identificationNum);
          const countriesResponse = await getUserCountries(user.identificationNum);
          return { ...user, projects: projectsResponse, countries: countriesResponse.map((c) => c.id) };
        })
      );

      setUsers(userWithProjects);

      // Proyectos de TODOS los países (para resolver nombres sin importar el país activo),
      // pero solo los ACTIVOS: así el mantenedor solo muestra/permite asignar proyectos vigentes.
      const projectsData = await getProjectsAllCountries();
      // Activos y solo de los países que el usuario administra (Gerente limitado; Admin Sistema = todos).
      setProjects(projectsData.filter((p) => p.status === true && !!p.country && myCountryIds.has(p.country.id)));
    } catch (error) {
      console.error('Error al obtener los usuarios:', error);
    }
  };

  const simplifiedProjects = projects
    .filter(project =>
      project.id !== undefined &&
      project.name !== undefined &&
      project.status === true
    )
    .map(({ id, name }) => ({ id: id as number, name: name as string }));

  const handleAddUser = async (newUser: User) => {
    try {
      const { projects, countries: userCountries, ...userWithoutProjects } = newUser;
      await createUser(userWithoutProjects);

      if (Array.isArray(projects) && projects.length > 0) {
        for (const projectId of projects) {
          const project = {
            id_user_proyect: 1,
            identificationNum: newUser.identificationNum,
            project: { id: projectId },
          };
          await ProjectsToUser(project);
        }
      }

      // Multi-país: asigna los países seleccionados al usuario.
      if (Array.isArray(userCountries) && userCountries.length > 0) {
        await updateUserCountries(newUser.identificationNum, userCountries);
      }

      await fetchUsers();
    } catch (error) {
      throw error;
    }
  };

  // Un perfil distinto a Admin de Sistema no puede modificar a un Admin de Sistema.
  const isTargetAdminSistema = (target: User): boolean =>
    roles.find(r => r.id === target.profile)?.description === ADMIN_SISTEMA;

  const handleEditUser = async (oldRut: string, updatedUser: User) => {
    if (isTargetAdminSistema(updatedUser) && !isAdminSistema) {
      console.warn('Solo un Administrador de Sistema puede editar a otro Administrador de Sistema.');
      return;
    }
    if (updatedUser.identificationNum !== undefined) {
      try {
        // 1. Actualizar datos base y reemplazar RUT (si aplica)
        await updateUser(oldRut, updatedUser);

        // 2. Guardar los proyectos de forma sincrónica con el nuevo RUT
        if (updatedUser.projects) {
          await updateUserProjects(updatedUser.identificationNum, updatedUser.projects);
        }

        // 3. Multi-país: guardar los países asignados con el nuevo RUT
        if (updatedUser.countries) {
          await updateUserCountries(updatedUser.identificationNum, updatedUser.countries);
        }
      } catch (error) {
        console.error('Error al editar el usuario:', error);
      }
    } else {
      console.error('El usuario no tiene un id válido para actualizar.');
    }

    // 4. Recargar la tabla SOLO cuando el RUT, Proyectos y Países ya están en la Base de Datos
    await fetchUsers();
  };

  const handleDeleteUser = async (userId: string, deleteUser1: User) => {
    if (!isManager) return;
    if (isTargetAdminSistema(deleteUser1) && !isAdminSistema) {
      console.warn('Solo un Administrador de Sistema puede eliminar a otro Administrador de Sistema.');
      return;
    }
    try {
      await deleteUser(deleteUser1.identificationNum);
      setUsers(users.filter(u => u.identificationNum !== userId));
    } catch (error) {
      console.error('Error al eliminar el usuario:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Usuarios
      </Typography>

      {isManager && (
        <UserForm
          onAddUser={handleAddUser}
          projects={simplifiedProjects}
          positions={positions}
          rols={roles}
          countries={countries.map(c => ({ id: c.id, name: c.name }))}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <UserTable
          users={users}
          projects={simplifiedProjects}
          positions={positions}
          countries={countries.map(c => ({ id: c.id, name: c.name }))}
          // Si no es gerente, pasamos undefined para que la tabla oculte los botones
          onEditUser={isManager ? handleEditUser : undefined}
          onDeleteUser={isManager ? handleDeleteUser : undefined}
          rols={roles}
          currentUserRole={userRole}
        />
      </Box>
    </Box>
  );
}

export default Usuarios;
